import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "./vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const berlineAvecChauffeur: ChauffeurPage = {
  slug: "berline-avec-chauffeur",
  title: "Berline avec chauffeur — déplacements d'affaires",
  subtitle:
    "Mercedes Classe E avec chauffeur privé : la berline d'affaires discrète sur la Côte d'Azur.",
  keywords: [
    "berline avec chauffeur Cannes",
    "Mercedes Classe E chauffeur",
    "VTC affaires Côte d'Azur",
    "chauffeur privé business Nice",
  ],
  hero: {
    title: "Berline avec chauffeur sur la Côte d'Azur — Mercedes Classe E",
    subtitle:
      "La berline d'affaires par excellence : discrétion, ponctualité et confort de travail pour vos déplacements professionnels du quotidien.",
    keywords: ["berline avec chauffeur", "Classe E", "VTC affaires"],
    imageSrc: heroImage.src,
    imageAlt: "Mercedes Classe E avec chauffeur privé à Cannes",
    badges: ["Jusqu'à 3 passagers", "Discrétion business", "Ponctualité"],
  },
  benefits: {
    heading: "Pourquoi la berline avec chauffeur KS Rent Services",
    items: [
      {
        title: "Discrétion business",
        description:
          "Une berline sobre et élégante, un chauffeur en tenue : l'image juste pour vos rendez-vous professionnels et vos clients.",
      },
      {
        title: "Ponctualité",
        description:
          "Horaires tenus, suivi des vols et anticipation du trafic : vous arrivez à l'heure, sans stress, à chaque rendez-vous.",
      },
      {
        title: "Confort de travail",
        description:
          "Habitacle silencieux et confortable : passez vos appels, préparez vos réunions et avancez pendant que l'on conduit.",
      },
      {
        title: "Le juste tarif",
        description:
          "La porte d'entrée du chauffeur privé : tout le service haut de gamme, au tarif le plus accessible de notre flotte.",
      },
    ],
  },
  services: {
    heading: "Prestations de la berline d'affaires",
    services: [
      {
        title: "Rendez-vous d'affaires",
        text: "Déplacements professionnels en ville, enchaînement de réunions et trajets inter-villes sur la Côte d'Azur.",
      },
      {
        title: "Transferts aéroport",
        text: "Aéroport de Nice, gares et héliport de Monaco, en solo ou en duo, avec accueil et prise en charge des bagages.",
      },
      {
        title: "Mise à disposition",
        text: "Chauffeur et berline à l'heure ou à la journée pour vos tournées commerciales et vos agendas chargés.",
      },
      {
        title: "Événements professionnels",
        text: "Salons, congrès et soirées d'entreprise : un chauffeur dédié pour vos déplacements et ceux de vos invités.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & aéroport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Le chauffeur privé au service de votre efficacité",
    subheading: "Tout le confort d'un service haut de gamme, sans superflu.",
    paragraphs: [
      "La Mercedes Classe E réunit l'essentiel du chauffeur privé : une berline confortable et discrète, idéale pour vos déplacements d'affaires quotidiens et vos transferts.",
      "Vous gagnez un temps précieux : pendant que votre chauffeur gère l'itinéraire et le stationnement, vous restez concentré sur ce qui compte.",
      "Un interlocuteur unique, des forfaits transparents et la même exigence de ponctualité que sur l'ensemble de notre flotte.",
    ],
    bullets: [
      "Berline jusqu'à 3 passagers",
      "Forfaits aéroport transparents",
      "Disponibilité 24h/24, 7j/7",
    ],
  },
  process: {
    heading: "Comment réserver votre berline avec chauffeur",
    steps: [
      "Indiquez votre trajet, vos horaires et vos éventuelles étapes.",
      "Recevez un forfait clair ou une estimation au kilomètre.",
      "Affectation de la Classe E et de votre chauffeur dédié.",
      "Prise en charge ponctuelle, discrète et confortable.",
    ],
  },
  faq: [
    {
      q: "Quel véhicule pour la berline avec chauffeur ?",
      a: "Une Mercedes Classe E, berline d'affaires confortable pour jusqu'à 3 passagers, idéale pour les déplacements professionnels et les transferts.",
    },
    {
      q: "Quelle différence avec la Classe S ?",
      a: "La Classe E est notre berline d'affaires au meilleur rapport prestation/prix ; la Classe S est la berline de prestige, pour une expérience VIP et une clientèle exigeante.",
    },
    {
      q: "Assurez-vous les transferts aéroport ?",
      a: "Oui, depuis et vers l'aéroport de Nice notamment, avec des forfaits transparents et l'accueil personnalisé du chauffeur.",
    },
    vehiclePriceFaq("fr", "CLASS_E", "d'une berline avec chauffeur"),
    {
      q: "Comment réserver ?",
      a: "Indiquez-nous votre trajet et vos horaires : nous confirmons rapidement la berline et votre chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Mercedes Classe S avec chauffeur",
      href: "/chauffeur/classe-s-avec-chauffeur",
    },
    {
      label: "Van avec chauffeur (7 places)",
      href: "/chauffeur/van-avec-chauffeur",
    },
    { label: "Tous nos services chauffeur", href: "/chauffeur" },
    {
      label: "Location de voiture pour un mariage à Cannes",
      href: "/chauffeur/voiture-mariage-cannes",
    },
  ],
  cta: {
    title: "Un déplacement d'affaires à organiser ?",
    text: "Dites-nous votre trajet — devis rapide pour votre berline avec chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Demander un devis",
  },
  seo: {
    ...vehicleSeo(
      "fr",
      "CLASS_E",
      "Berline avec chauffeur Classe E, Côte d'Azur",
      "Berline Mercedes Classe E avec chauffeur",
    ),
    canonical: "/chauffeur/berline-avec-chauffeur",
  },
};

export default berlineAvecChauffeur;
